import t from '@jetshop/intl';
import Accordion from '@jetshop/ui/Accordion/Accordion';
import ProductSpecifications from '@jetshop/ui/ProductSpecifications/ProductSpecifications';
import React, { Fragment } from 'react';
import { styled } from 'linaria/react';
import {
  ProductSection as UIProductSection,
  SectionHeading
} from './ProductInfo';
import { theme } from '../Theme';
import { ReactComponent as Plus } from '../../svg/Plus.svg';
import { ReactComponent as Minus } from '../../svg/Minus.svg';

const Description = styled('article')`
  line-height: 25px;
  padding: 0 20px 10px 20px;
  h1,
  h2,
  h3,
  h4 {
    margin-bottom: 1rem;
  }
  h1 {
    line-height: 1.2;
  }
  h2 {
    line-height: 1.2;
  }
  p {
    font-size: 14px;
    line-height: 1.5;
    font-weight: ${theme.fontWeights.regular};
    color: ${theme.colors.black};
    margin-bottom: 1rem;
  }
  ul {
    list-style-type: disc;
    padding-left: 1rem;
    li {
      font-size: 14px;
      line-height: 1.5;
      font-weight: ${theme.fontWeights.regular};
      color: ${theme.colors.black};
    }
  }
  iframe {
    width: 560px;
    height: 315px;
    align-self: center;

    ${theme.below.sm} {
      width: 340px;
      height: 191px;
    }
  }
`;

const ProductSection = styled(UIProductSection)`
  width: 100%;
  border-bottom: 1px solid ${theme.colors.border};
  padding: 0;
  margin: 0;
  min-height: 51px;
  display: flex;
  flex-direction: column;

  :not(& + *) {
    /* Selects top item */
    border-top: 1px solid ${theme.colors.border};
    margin-top: 3rem;
  }

  ${SectionHeading} {
    width: 100%;
    display: flex;
    justify-items: space-between;
    align-items: center;
    margin-bottom: 0;
    padding: 15px 20px;
    height: 100%;
    font-size: ${theme.fontSizes.heading3};
    line-height: ${theme.fontSizes.heading3};
    font-weight: ${theme.fontWeights.regular};
    + * > :first-child {
      margin-top: 1rem;
    }
  }

  .read-more {
    margin: 0 20px 10px 20px;
    background: none;
    border: 0;
    appearance: none;
    color: ${theme.colors.black};
  }
`;

const IndicatorIcon = styled('span')`
  margin-left: auto;
  font-size: 1.25rem;
  svg {
    width: 14px;
    height: 14px;
  }
`;

const ProductSpecs = styled('div')`
  padding: 0 20px;
  font-size: 14px;
  tr {
    border-bottom: 1px solid #f7f7f7;
    &:last-child {
      border: 0;
    }
    > * {
      line-height: 1.8;
    }
  }
`;

const AccordionIndicator = ({ isOpen }) => (
  <IndicatorIcon>{isOpen ? <Minus /> : <Plus />}</IndicatorIcon>
);

const ProductInfoAccordion = ({ product }) => {
  const excludedCustomFields = [
    'video',
    'relatedProducts',
    'colorVariants',
    'sizeguide',
    'msrp',
    'upsellProducts'
  ];

  const filteredCustomFields = product.customFields.filter(
    customField => !excludedCustomFields.includes(customField.key)
  );

  return (
    <Accordion initialOpenIndexes={[0]}>
      {({ openIndexes, handleClick, AccordionContent }) => (
        <Fragment>
          <ProductSection>
            <SectionHeading onClick={() => handleClick(0)}>
              <span>{t('Product description')}</span>
              <AccordionIndicator isOpen={openIndexes.includes(0)} />
            </SectionHeading>
            <AccordionContent isOpen={openIndexes.includes(0)}>
              <Description
                dangerouslySetInnerHTML={{
                  __html: product?.description
                }}
              />
            </AccordionContent>
          </ProductSection>
          <ProductSection>
            <SectionHeading onClick={() => handleClick(1)}>
              <span>{t('Specifications')}</span>
              <AccordionIndicator isOpen={openIndexes.includes(1)} />
            </SectionHeading>
            <AccordionContent isOpen={openIndexes.includes(1)}>
              <ProductSpecs>
                <ProductSpecifications fields={filteredCustomFields} />
              </ProductSpecs>
            </AccordionContent>
          </ProductSection>
        </Fragment>
      )}
    </Accordion>
  );
};

export default ProductInfoAccordion;
